import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── SVG Semi-circle Gauge ────────────────────────────────────────────────────
function SemiGauge({ value, color, label, sublabel }) {
  const r = 38;
  const cx = 60;
  const cy = 58;
  const fullCirc = 2 * Math.PI * r;       // ≈ 238.76
  const semi = Math.PI * r;               // ≈ 119.38 (the visible arc)
  const pct = Math.min(Math.max(value, 0), 100) / 100;
  const filled = semi * pct;
  const gap = fullCirc - filled;

  // Color based on value
  const arcColor = value >= 70 ? '#16a34a' : value >= 40 ? color : value >= 15 ? '#f59e0b' : '#ef4444';

  return (
    <div className="flex flex-col items-center" data-testid={`pulse-gauge-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
      <svg width="120" height="74" viewBox="0 0 120 74" style={{ overflow: 'visible' }}>
        {/* Shadow / glow behind track */}
        <defs>
          <filter id={`glow-${label}`} x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
        </defs>

        {/* Background track (gray semicircle) */}
        <circle
          cx={cx} cy={cy} r={r}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="11"
          strokeDasharray={`${semi} ${fullCirc - semi}`}
          strokeDashoffset="0"
          transform={`rotate(-180 ${cx} ${cy})`}
          strokeLinecap="round"
        />

        {/* Filled arc */}
        <circle
          cx={cx} cy={cy} r={r}
          fill="none"
          stroke={arcColor}
          strokeWidth="11"
          strokeDasharray={`${filled} ${gap}`}
          strokeDashoffset="0"
          transform={`rotate(-180 ${cx} ${cy})`}
          strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 0.8s ease' }}
        />

        {/* Center value */}
        <text
          x={cx} y={cy - 4}
          textAnchor="middle"
          style={{ fontSize: '18px', fontWeight: '800', fill: '#111827', fontFamily: 'inherit' }}
        >
          {value}%
        </text>

        {/* Small dot markers at ends */}
        <circle cx={cx - r} cy={cy} r="4" fill="#e5e7eb" />
        <circle cx={cx + r} cy={cy} r="4" fill="#e5e7eb" />
      </svg>

      {/* Label */}
      <p className="text-xs font-bold text-stone-700 text-center leading-tight mt-0.5">{label}</p>
      {sublabel && (
        <p className="text-[10px] text-stone-400 text-center leading-tight mt-0.5">{sublabel}</p>
      )}
    </div>
  );
}

// ─── Company Pulse Strip ──────────────────────────────────────────────────────
export default function CompanyPulseStrip() {
  const [gauges, setGauges] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchPulse = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const token = localStorage.getItem('crm_token');
      const res = await axios.get(`${API}/analytics/cmd-pulse`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGauges(res.data.gauges || []);
      setUpdatedAt(res.data.updated_at);
    } catch (err) {
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchPulse();
    // Auto-refresh every 5 minutes
    const interval = setInterval(() => fetchPulse(), 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchPulse]);

  const formatTime = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div
      className="relative rounded-xl border border-stone-200 bg-white shadow-sm overflow-hidden"
      data-testid="company-pulse-strip"
    >
      {/* Header bar */}
      <div className="flex items-center justify-between px-5 pt-3 pb-2 border-b border-stone-100">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-xs font-bold tracking-widest text-stone-500 uppercase">
            Company Pulse
          </span>
        </div>
        <div className="flex items-center gap-3">
          {updatedAt && (
            <span className="text-[10px] text-stone-400">
              Updated {formatTime(updatedAt)}
            </span>
          )}
          <button
            onClick={() => fetchPulse(true)}
            disabled={refreshing}
            className="p-1 rounded hover:bg-stone-100 transition-colors disabled:opacity-40"
            data-testid="pulse-refresh-btn"
            title="Refresh pulse"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-stone-400 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Gauges row */}
      <div className="px-4 py-3">
        {loading ? (
          <div className="flex items-center justify-center h-24 gap-2 text-stone-400">
            <div className="w-4 h-4 border-2 border-stone-300 border-t-emerald-500 rounded-full animate-spin" />
            <span className="text-xs">Loading pulse data…</span>
          </div>
        ) : (
          <div className="grid grid-cols-5 gap-2 divide-x divide-stone-100">
            {gauges.map((g) => (
              <div key={g.id} className="flex items-center justify-center py-1">
                <SemiGauge
                  value={g.value}
                  color={g.color}
                  label={g.label}
                  sublabel={g.sublabel}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Thin colored bottom border as accent */}
      <div className="h-1 w-full bg-gradient-to-r from-emerald-400 via-blue-400 via-purple-400 via-amber-400 to-pink-400" />
    </div>
  );
}
